import type {
    PendingDropdownItem,
    RepeatablePendingDropdownResolutionTarget,
} from './repeatable-group.types';

export type RepeatablePendingDropdownCatalogEventSource = 'dataSource' | 'loadData';

export type RepeatablePendingDropdownCatalogEventInput = {
    logicalKey: string;
    rowIndex?: number;
    source: RepeatablePendingDropdownCatalogEventSource;
    optionCount?: number;
};

export type RepeatablePendingDropdownCatalogEvent = {
    logicalKey: string;
    source: RepeatablePendingDropdownCatalogEventSource;
    loadedAt: number;
    rowIndex?: number;
    optionCount?: number;
};

export type RepeatablePendingDropdownCatalogEventConsumeResult = {
    targets: RepeatablePendingDropdownResolutionTarget[];
    matchedEvents: RepeatablePendingDropdownCatalogEvent[];
    droppedEvents: RepeatablePendingDropdownCatalogEvent[];
};

/**
 * Keeps catalog-loaded events separate from timer retries.
 *
 * The repeatable component records dataSource/loadData completions here and consumes them as targeted pending-dropdown
 * resolution requests. That makes option availability the primary signal, while the retry scheduler remains a legacy
 * fallback for option sources that still do not emit a reliable completion event.
 */
export class RepeatablePendingDropdownCatalogEventQueue {
    private readonly events: RepeatablePendingDropdownCatalogEvent[] = [];

    public record(input: RepeatablePendingDropdownCatalogEventInput): void {
        const logicalKey = input.logicalKey.trim();

        if (logicalKey === '') {
            return;
        }

        const rowIndex = this.normalizeRowIndex(input.rowIndex);
        const optionCount = this.normalizeOptionCount(input.optionCount);
        const event: RepeatablePendingDropdownCatalogEvent = {
            logicalKey,
            source: input.source,
            loadedAt: Date.now(),
            ...(rowIndex !== null ? { rowIndex } : {}),
            ...(optionCount !== null ? { optionCount } : {}),
        };

        this.events.push(event);
    }

    public clear(): void {
        this.events.splice(0, this.events.length);
    }

    public snapshot(): RepeatablePendingDropdownCatalogEvent[] {
        return [...this.events];
    }

    public consumeTargetsForPending(
        pendingDropdown: readonly PendingDropdownItem[],
    ): RepeatablePendingDropdownCatalogEventConsumeResult {
        if (this.events.length === 0) {
            return {
                targets: [],
                matchedEvents: [],
                droppedEvents: [],
            };
        }

        const matchedEvents: RepeatablePendingDropdownCatalogEvent[] = [];
        const droppedEvents: RepeatablePendingDropdownCatalogEvent[] = [];

        for (const event of this.events) {
            if (this.hasMatchingPendingDropdown(event, pendingDropdown)) {
                matchedEvents.push(event);
            } else {
                droppedEvents.push(event);
            }
        }

        this.clear();

        return {
            targets: this.createTargets(matchedEvents),
            matchedEvents,
            droppedEvents,
        };
    }

    private hasMatchingPendingDropdown(
        event: RepeatablePendingDropdownCatalogEvent,
        pendingDropdown: readonly PendingDropdownItem[],
    ): boolean {
        return pendingDropdown.some((item) => {
            const matchesLogicalKey = item.logicalKey === event.logicalKey;
            const matchesRowIndex = event.rowIndex === undefined || item.rowIndex === event.rowIndex;

            return matchesLogicalKey && matchesRowIndex;
        });
    }

    private createTargets(events: RepeatablePendingDropdownCatalogEvent[]): RepeatablePendingDropdownResolutionTarget[] {
        const targets: RepeatablePendingDropdownResolutionTarget[] = [];

        for (const event of events) {
            const target = this.createTarget(event);
            const isAlreadyAdded = targets.some((current) =>
                current.logicalKey === target.logicalKey && current.rowIndex === target.rowIndex);

            if (!isAlreadyAdded) {
                targets.push(target);
            }
        }

        return targets;
    }

    private createTarget(event: RepeatablePendingDropdownCatalogEvent): RepeatablePendingDropdownResolutionTarget {
        return {
            logicalKey: event.logicalKey,
            ...(event.rowIndex !== undefined ? { rowIndex: event.rowIndex } : {}),
        };
    }

    private normalizeRowIndex(rowIndex: number | undefined): number | null {
        if (!Number.isInteger(rowIndex) || Number(rowIndex) < 0) {
            return null;
        }

        return Number(rowIndex);
    }

    private normalizeOptionCount(optionCount: number | undefined): number | null {
        if (!Number.isInteger(optionCount) || Number(optionCount) < 0) {
            return null;
        }

        return Number(optionCount);
    }
}
